import {
  autorun,
  makeAutoObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {
  CardApiModel,
  PaymentExpandedCommon,
} from '../../../../api/payment';
import type {CoreCoachPaymentsAddCommissionParams} from '../../../../api/types/core/methods';
import type {TextInput} from '../../../../uikit/TextInput';
import type {
  Handle3dSecureFn,
  ReturnUrl,
} from '../../../../utils/entity/payment/handleRequiresActionPayment';
import type {
  Destructible,
  WithBusyManager,
  WithReactionDispose,
} from '../../../../utils/store';
import type {GetComponentProps} from '../../../../utils/utility-types';
import type {
  AvailableAmountToRefund,
  RefundBy,
  RefundSuccessEventData,
} from './types';

import {cardStore, paymentStore} from '../../../../api/payment';
import {apiRequest} from '../../../../api/utils/apiRequest';
import {alert} from '../../../../components/alert';
import {COMMAND} from '../../../../services/entity-action/utils';
import {handleRequiresActionPayment} from '../../../../utils/entity/payment';
import {emitter} from '../../../../utils/eventEmitter';
import {eventPayloadActionCreator} from '../../../../utils/events';
import {logger} from '../../../../utils/logger';
import {
  Currency,
  getPriceString,
  parseAmountString,
} from '../../../../utils/money';
import {
  AsyncBusyManager,
  ReactionDisposeManager,
} from '../../../../utils/store';
import {
  createTextInputField,
  validateFields,
} from '../../../../utils/validation';
import {PaymentModel} from '../../Payment.model';
import {MIN_REFUND_AMOUNT} from '../../utils';
import {
  getAvailableAmountToRefund,
  MAKE_REFUND_FAIL_EVENT,
  MAKE_REFUND_SUCCESS_EVENT,
} from './utils';

type ReasonKey = 'fetchTax' | 'makeRefund';

export class MakeRefundStore
  implements Destructible, WithBusyManager<ReasonKey>, WithReactionDispose
{
  amount = 0;
  readonly busy = new AsyncBusyManager<ReasonKey>();
  cardError = '';
  disposer = new ReactionDisposeManager();
  readonly fields = this.makeFields();
  isNoteFieldVisible = false;
  isTaxFetching = false;
  payment: PaymentModel;
  refundBy: RefundBy = 'stripe';
  selectedCard: CardApiModel | null = null;
  private amountWithTax = 0;
  private availableAmountToRefund: AvailableAmountToRefund = {
    byCard: 0,
    byStripe: 0,
  };
  private currency = Currency.USD;
  private fetchTaxTimeout = 0;

  constructor(payment: PaymentExpandedCommon) {
    this.payment = new PaymentModel(payment);

    this.availableAmountToRefund = getAvailableAmountToRefund(payment);

    this.refundBy =
      this.availableAmountToRefund.byStripe === 0 ? 'card' : 'stripe';

    this.currency = Currency[payment.currency];

    this.busy.observe(this, {
      fetchTax: 'fetchTax',
    });

    this.busy.register('makeRefund');

    makeAutoObservable<MakeRefundStore, 'fetchTaxTimeout'>(
      this,
      {
        fetchTaxTimeout: false,
        selectedCard: observable.ref,
      },
      {
        autoBind: true,
      },
    );

    this.setupReactions();
  }

  destruct() {
    clearTimeout(this.fetchTaxTimeout);

    this.disposer.dispose();
  }

  get amountInputPlaceholder() {
    return i18n.t('shared:enter_amount_between', {
      max_amount: getPriceString({
        currency: this.currency,
        price: this.maxAmount,
      }),
      min_amount: getPriceString({
        currency: this.currency,
        price: MIN_REFUND_AMOUNT,
      }),
    });
  }

  get amountInputValue() {
    if (!this.fields.amount.value) {
      return '';
    }

    return `${this.currency.sign}${this.fields.amount.value}`;
  }

  get amountWithTaxString() {
    return this.busy.getReason('fetchTax') || this.amountWithTax <= 0
      ? i18n.t('shared:ellipsis')
      : getPriceString({
          currency: this.currency,
          price: this.amountWithTax,
        });
  }

  get cards() {
    return Object.keys(cardStore.cards).map(key => cardStore.cards[key]!);
  }

  get isFormValid() {
    return this.validate();
  }

  get isRefundByStripeAvailable() {
    return this.availableAmountToRefund.byStripe === 0;
  }

  get maxAmount() {
    if (this.refundBy === 'stripe') {
      return this.availableAmountToRefund.byStripe;
    }

    return this.availableAmountToRefund.byCard;
  }

  get taxString() {
    return this.busy.getReason('fetchTax') || this.amountWithTax <= 0
      ? i18n.t('shared:ellipsis')
      : getPriceString({
          currency: this.currency,
          price: this.amountWithTax - this.amount,
        });
  }

  fetchTax(amount: number) {
    clearTimeout(this.fetchTaxTimeout);

    this.isTaxFetching = true;

    this.fetchTaxTimeout = +setTimeout(async () => {
      const result: {_total: number} =
        await apiRequest<CoreCoachPaymentsAddCommissionParams>({
          method: 'coach.payments.add_commission',
          params: {
            amount: amount * 100,
          },
        });

      runInAction(() => {
        this.amountWithTax = result._total;
        this.isTaxFetching = false;
      });
    }, 1000);
  }

  async makeRefund({
    handle3dSecure,
    returnUrl,
  }: {
    handle3dSecure: Handle3dSecureFn;
    returnUrl: ReturnUrl;
  }) {
    if (!this.validate()) {
      return COMMAND.CANCEL;
    }

    if (!(await alert.confirm({confirmButtonScheme: 'secondary'}))) {
      return COMMAND.CANCEL;
    }

    try {
      this.busy.setReason('makeRefund', true);

      if (this.refundBy === 'card') {
        await this.refundByCard({handle3dSecure, returnUrl});
      } else {
        await this.refundByStripe();
      }

      /**
       * Need to react in other places.
       * Course payments tab for example
       */
      const eventData: RefundSuccessEventData = {payment: this.payment.payment};

      emitter.emit(MAKE_REFUND_SUCCESS_EVENT, eventData);
    } catch (error) {
      this.busy.setReason('makeRefund', false);

      emitter.emit(MAKE_REFUND_FAIL_EVENT);

      throw error;
    }
  }

  onChangeAmount(
    e: Parameters<GetComponentProps<typeof TextInput>['onChange']>[0],
  ) {
    let amountText = (e.payload?.text || '')
      .replace(this.currency.sign, '')
      // allow to enter only digits here
      .replace(/[^\d.,]/g, '');

    amountText = parseAmountString(amountText);

    this.amount = amountText
      ? (parseFloat(amountText.replace(',', '.')) * 1000) / 10
      : 0;

    this.fields.amount.onChange!(
      eventPayloadActionCreator({text: amountText}, e.event),
    );
  }

  setNoteFieldVisibility(isVisible: boolean) {
    this.isNoteFieldVisible = isVisible;
  }

  setRefundBy(value: RefundBy) {
    this.refundBy = value;
  }

  setSelectedCard(card: CardApiModel) {
    this.selectedCard = card;
  }

  private makeFields() {
    return {
      amount: createTextInputField('amount', {
        customValidate: () => {
          const field = this.fields.amount;

          let isValid = true;

          field.error = null;

          if (
            (this.amount > 0 && this.amount < MIN_REFUND_AMOUNT) ||
            !this.amount
          ) {
            field.error = i18n.t('shared:min_amount', {
              value: getPriceString({
                currency: this.currency,
                price: MIN_REFUND_AMOUNT,
              }),
            });
            isValid = false;
          } else if (this.amount > this.maxAmount) {
            field.error = i18n.t('shared:max_amount_max_value', {
              value: getPriceString({
                currency: this.currency,
                price: this.maxAmount,
              }),
            });
            isValid = false;
          }

          field.isValid = isValid;

          return isValid;
        },
        validationRule: {
          messages: {
            required: i18n.t('shared:amount_required'),
          },
          type: 'string',
        },
      }),
      note: createTextInputField('note', {
        defaultValue: '',
        validationRule: {
          max: 255,
          messages: {
            stringMax: i18n.t('shared:note_max_length', {
              maxLength: 255,
            }),
          },
          optional: true,
          type: 'string',
        },
      }),
    };
  }

  private async refundByCard({
    handle3dSecure,
    returnUrl,
  }: {
    handle3dSecure: Handle3dSecureFn;
    returnUrl: ReturnUrl;
  }) {
    try {
      const result = await paymentStore.coach.createRefundPayment(
        this.payment.toJS,
        {
          amount: this.amount,
          card_id: this.selectedCard!._id,
          description: this.fields.note.value.trim(),
        },
      );

      await handleRequiresActionPayment({
        card: this.selectedCard!,
        clientSecret: result.clientSecret,
        handle3dSecure,
        payment: result.payment,
        returnUrl,
      });
    } catch (error) {
      logger.error(error);

      throw error;
    }
  }

  private async refundByStripe() {
    try {
      await paymentStore.coach.refund(this.payment.toJS, {
        amount: this.amount,
        description: this.fields.note.value.trim(),
      });
    } catch (error) {
      logger.error(error);

      throw error;
    }
  }

  private setupReactions() {
    this.disposer.watch(
      'refundBy',
      autorun(() => {
        runInAction(() => {
          this.amountWithTax = 0;
        });

        const enteredAmount = parseFloat(
          this.fields.amount.value.replace(',', '.'),
        );

        if (
          this.refundBy === 'card' &&
          enteredAmount * 100 >= MIN_REFUND_AMOUNT
        ) {
          this.fetchTax(enteredAmount);
        }
      }),
    );

    this.disposer.watch(
      'cards',
      reaction(
        () => this.cards,
        cards => {
          if (cards.length === 1) {
            this.selectedCard = cards[0]!;
          }
        },
        {fireImmediately: true},
      ),
    );

    this.disposer.watch(
      'maxAmount',
      reaction(
        () => this.maxAmount,
        () => {
          this.fields.amount.validate();
        },
      ),
    );

    this.disposer.watch(
      'cards',
      reaction(
        () => this.selectedCard,
        selectedCard => {
          if (selectedCard) {
            this.cardError = '';
          }
        },
      ),
    );
  }

  private validate() {
    const fieldsAreValid = validateFields(this.fields);

    if (this.refundBy === 'card' && !this.selectedCard) {
      this.cardError = i18n.t('shared:choose_card');

      return false;
    }

    return fieldsAreValid;
  }
}
